import { useQuery } from '@tanstack/react-query';

import { useDocumentSets } from '../../../hooks/useDocumentSets';
import { useMetaphysicsKg, type IKgOwner } from '../../../lib/metaphysics-kg';

import type { INodeConfigProps } from './index';

const ragModes = ['naive', 'local', 'global', 'hybrid', 'mix'] as const;
type RagMode = (typeof ragModes)[number];

type KgOwnerKind = 'pipeline' | 'documentSet';

interface IKgOwnerOption {
  kind: KgOwnerKind;
  id: string;
  name: string;
}

/**
 * `rag-retrieve` nodes query a Metaphysics knowledge graph via LightRAG.
 * Mirrors the runtime schema in `packages/node-types/src/rag-retrieve/`:
 *   - exactly one of `pipelineId` or `documentSetId` — the KG owner
 *   - `kgVersion` (optional) — pin a specific KG version on that owner
 *   - `mode` (default 'mix') — LightRAG retrieval mode
 *   - `artifactNames` (optional) — comma-joined filter; parsed to array
 *   - `includeChunks` (default false) — return chunk text per reference
 *
 * The KG-source picker is a single combo box backed by `optgroup`s for
 * pipelines and document sets. Switching kinds clears the other id so the
 * runtime's XOR refine stays satisfied. A saved id no longer in either list
 * remains visible as a synthetic option (orphan) so the form is still
 * editable.
 */
export function RagRetrieveConfig({ config, onChange }: INodeConfigProps) {
  const pipelineId = typeof config.pipelineId === 'string' ? config.pipelineId : '';
  const documentSetId = typeof config.documentSetId === 'string' ? config.documentSetId : '';
  const kgVersion =
    typeof config.kgVersion === 'number' && Number.isFinite(config.kgVersion)
      ? config.kgVersion
      : '';
  const mode: RagMode = (ragModes as readonly string[]).includes(config.mode as string)
    ? (config.mode as RagMode)
    : 'mix';
  const artifactNames = Array.isArray(config.artifactNames)
    ? (config.artifactNames as string[]).join(', ')
    : '';
  const includeChunks = config.includeChunks === true;

  const kg = useMetaphysicsKg();
  const pipelinesQuery = useQuery({
    queryKey: ['mp', 'kg', 'pipelines'] as const,
    queryFn: () => kg.listPipelines(),
  });
  const documentSetsQuery = useDocumentSets();

  const owner: IKgOwner | null = pipelineId
    ? { kind: 'pipeline', pipelineId }
    : documentSetId
      ? { kind: 'documentSet', documentSetId }
      : null;
  const versionsQuery = useQuery({
    queryKey: [
      'mp',
      'kg',
      'versions',
      owner?.kind,
      owner?.kind === 'pipeline' ? owner.pipelineId : owner?.documentSetId,
    ] as const,
    queryFn: () => {
      if (!owner) throw new Error('versions query enabled without owner');
      return kg.listVersions(owner);
    },
    enabled: owner !== null,
  });
  // Newest version first — matches the metaphysics-kg client default ordering
  // for documentSet rows, but pipeline listVersions returns them as stored.
  // Sorting client-side keeps the dropdown stable regardless of source.
  const versions = (versionsQuery.data ?? []).slice().sort((a, b) => b.version - a.version);

  const pipelines: IKgOwnerOption[] = (pipelinesQuery.data ?? []).map((p) => ({
    kind: 'pipeline',
    id: p.id,
    name: p.name,
  }));
  const documentSets: IKgOwnerOption[] = (documentSetsQuery.data ?? []).map((d) => ({
    kind: 'documentSet',
    id: d.id,
    name: d.name,
  }));

  const isLoading = pipelinesQuery.isLoading || documentSetsQuery.isLoading;

  const selectedKind: KgOwnerKind | '' = pipelineId
    ? 'pipeline'
    : documentSetId
      ? 'documentSet'
      : '';
  const selectedId = pipelineId || documentSetId;
  const selectedValue = selectedKind && selectedId ? `${selectedKind}:${selectedId}` : '';

  // If the saved id is no longer present in either list, surface it as an
  // orphan option so the form remains editable + the value renders. This
  // avoids the select silently snapping to the empty placeholder and
  // overwriting the user's saved config on a parent re-render.
  const knownInLists =
    selectedKind === 'pipeline'
      ? pipelines.some((p) => p.id === selectedId)
      : selectedKind === 'documentSet'
        ? documentSets.some((d) => d.id === selectedId)
        : true;
  const showOrphan = !isLoading && selectedKind !== '' && !knownInLists;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <label className="field">
        <span className="field-label">KG source</span>
        <select
          className="select-native"
          value={selectedValue}
          disabled={isLoading}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw) {
              onChange({ pipelineId: undefined, documentSetId: undefined });
              return;
            }
            const [kind, ...rest] = raw.split(':');
            const id = rest.join(':');
            if (kind === 'pipeline') {
              onChange({ pipelineId: id, documentSetId: undefined });
            } else if (kind === 'documentSet') {
              onChange({ documentSetId: id, pipelineId: undefined });
            }
          }}
        >
          <option value="" disabled={true}>
            {isLoading ? 'Loading…' : 'Select a pipeline or document set'}
          </option>
          {showOrphan && (
            <option value={selectedValue}>
              {selectedKind === 'pipeline' ? 'Pipeline' : 'Document set'} · {selectedId} (not found)
            </option>
          )}
          {pipelines.length > 0 && (
            <optgroup label="Pipelines">
              {pipelines.map((p) => (
                <option key={`pipeline:${p.id}`} value={`pipeline:${p.id}`}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          )}
          {documentSets.length > 0 && (
            <optgroup label="Document sets">
              {documentSets.map((d) => (
                <option key={`documentSet:${d.id}`} value={`documentSet:${d.id}`}>
                  {d.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <span className="field-help">
          Pick a Metaphysics <code className="t-mono">PipelineConfig</code> or{' '}
          <code className="t-mono">DocumentSet</code>. The runtime resolves the workspace from
          whichever you pick.
          {selectedKind && selectedId && (
            <>
              {' '}
              Current ID: <code className="t-mono">{selectedId}</code>
            </>
          )}
        </span>
      </label>

      <label className="field">
        <span className="field-label">KG version</span>
        <select
          className="select-native"
          value={kgVersion === '' ? '' : String(kgVersion)}
          disabled={owner === null || versionsQuery.isLoading}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange({ kgVersion: undefined });
              return;
            }
            const next = Number.parseInt(raw, 10);
            onChange({ kgVersion: Number.isFinite(next) ? next : undefined });
          }}
        >
          <option value="">
            {owner === null
              ? 'Pick a KG source first'
              : versionsQuery.isLoading
                ? 'Loading…'
                : versions.length === 0
                  ? 'Latest (no versions yet)'
                  : `Latest (v${versions[0]?.version})`}
          </option>
          {/* Pinned-but-unknown version surfaces as an orphan so the form
              remains editable when the version list lags or has been pruned. */}
          {kgVersion !== '' && !versions.some((v) => v.version === kgVersion) && (
            <option value={String(kgVersion)}>v{kgVersion} (not found)</option>
          )}
          {versions.map((v) => (
            <option key={v.id} value={String(v.version)}>
              v{v.version}
              {v.createdAt ? ` · ${new Date(v.createdAt).toLocaleDateString()}` : ''}
            </option>
          ))}
        </select>
        <span className="field-help">
          Pin to a specific KG version (<code className="t-mono">PipelineManifest</code> or{' '}
          <code className="t-mono">DocumentSetKgVersion</code> depending on owner). Leave on{' '}
          <em>Latest</em> to track the newest version.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Mode</span>
        <select
          className="select-native"
          value={mode}
          onChange={(e) => onChange({ mode: e.target.value as RagMode })}
        >
          {ragModes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <span className="field-help">
          LightRAG retrieval mode. <code className="t-mono">hybrid</code> is the recommended
          default; <code className="t-mono">naive</code> skips graph traversal.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Artifact names (optional)</span>
        <input
          className="input"
          value={artifactNames}
          onChange={(e) => {
            const list = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            onChange({ artifactNames: list.length > 0 ? list : undefined });
          }}
          placeholder="comma-separated list (leave blank for all)"
        />
        <span className="field-help">
          Citation-side filter — restricts returned references to these artifact names.
        </span>
      </label>

      <label
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          className="checkbox"
          checked={includeChunks}
          onChange={(e) => onChange({ includeChunks: e.target.checked })}
        />
        <span className="t-sm">Include chunk text for each cited reference</span>
      </label>
    </div>
  );
}
